import React from 'react';
import { Users, Plus, Edit, Trash2 } from 'lucide-react';
import UserPermissionsPanel from './UserPermissionsPanel';

const getRoleBadgeColor = (role) => {
  const colors = { admin: 'bg-red-100 text-red-800', plant_manager: 'bg-blue-100 text-blue-800',
    operator: 'bg-green-100 text-green-800', viewer: 'bg-gray-100 text-gray-800' };
  return colors[role] || 'bg-gray-100 text-gray-800';
};

const getUserSubmitLabel = (loading, editingUser) => {
  if (loading) return 'Saving...';
  return editingUser ? 'Update User' : 'Create User';
};

export const UserForm = ({ formData, setFormData, editingUser, plants, loading, onSubmit, onCancel }) => (
  <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 border-2 border-[#156ACA]/20">
    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4">{editingUser ? 'Edit User' : 'Add New User'}</h3>
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Username <span className="text-red-500">*</span></label>
          <input type="text" value={formData.username} onChange={(e) => setFormData({ ...formData, username: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#156ACA]" placeholder="Enter username"
            required disabled={!!editingUser} data-testid="user-username-input" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Email <span className="text-red-500">*</span></label>
          <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#156ACA]" placeholder="Enter email"
            required data-testid="user-email-input" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Password {!editingUser && <span className="text-red-500">*</span>}</label>
          <input type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#156ACA]"
            placeholder={editingUser ? 'Leave blank to keep current' : 'Enter password'} required={!editingUser} data-testid="user-password-input" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Role <span className="text-red-500">*</span></label>
          <select value={formData.role} onChange={(e) => setFormData({ ...formData, role: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#156ACA]" data-testid="user-role-select">
            <option value="admin">Admin (Full Access)</option>
            <option value="plant_manager">Plant Manager</option>
            <option value="operator">Operator (Data Entry)</option>
            <option value="viewer">Viewer (Read Only)</option>
          </select>
        </div>
      </div>

      {formData.role !== 'admin' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Assign Plants</label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {plants.map(plant => (
              <label key={plant.id} className="flex items-center p-2 border rounded-lg hover:bg-gray-50 cursor-pointer">
                <input type="checkbox" checked={formData.assigned_plants.includes(plant.id)}
                  onChange={() => setFormData(prev => ({
                    ...prev,
                    assigned_plants: prev.assigned_plants.includes(plant.id)
                      ? prev.assigned_plants.filter(id => id !== plant.id)
                      : [...prev.assigned_plants, plant.id]
                  }))} className="mr-2" />
                <span className="text-sm">{plant.name}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:space-x-3 sm:gap-0">
        <button type="button" onClick={onCancel} className="px-6 py-2.5 min-h-[44px] border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">Cancel</button>
        <button type="submit" disabled={loading} className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2.5 px-6 min-h-[44px] rounded-lg transition-colors disabled:opacity-50" data-testid="save-user-btn">
          {getUserSubmitLabel(loading, editingUser)}
        </button>
      </div>
    </form>
  </div>
);

export const UserTable = ({ users, loading, onEdit, onDelete, token, onReload }) => (
  <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
    <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Users ({users.length})</h3>
    {loading && users.length === 0 ? (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#156ACA]"></div>
      </div>
    ) : (
      <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plants</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users.map(user => (
              <React.Fragment key={user.id}>
                <tr data-testid={`user-row-${user.username}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center">
                      <Users className="h-5 w-5 text-gray-400 mr-2" />
                      <span className="font-medium">{user.username}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{user.email}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getRoleBadgeColor(user.role)}`}>
                      {user.role.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {user.role === 'admin' ? 'All Plants' : (user.assigned_plants?.length || 0) + ' plants'}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${user.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {user.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => onEdit(user)} className="text-orange-600 hover:text-orange-800 mr-3" data-testid={`edit-user-${user.username}`}>
                      <Edit className="h-4 w-4 inline" />
                    </button>
                    {user.username !== 'admin' && (
                      <button onClick={() => onDelete(user.id)} className="text-red-600 hover:text-red-800" data-testid={`delete-user-${user.username}`}>
                        <Trash2 className="h-4 w-4 inline" />
                      </button>
                    )}
                  </td>
                </tr>
                <tr>
                  <td colSpan={6} className="px-4 pb-3">
                    <UserPermissionsPanel user={user} token={token} onSaved={onReload} />
                  </td>
                </tr>
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);
